import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Image } from 'react-native';
import { Icons } from '../../Components';
import { ProfileApi, ProfileHelpers } from '../api';
import type { CodeRequestProps, FocusPositionProps, MyPlayerProps, PlayerReferralProps, PromoProps, PublicPlayerProps, RewardOptionProps } from '../../types';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type CodeRedeemProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    onVerify:() => void,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onRedeemCode:(code_details:{
        code_request?:CodeRequestProps,
        promo?:PromoProps,
        player_referral?:PlayerReferralProps,
        reward_option?:RewardOptionProps,
        referrer?:PublicPlayerProps
    }) => void,
    onClose:() => void
}

const sections = ['header','code_used','redeemed','referrer']
const CodeRedeem = ({ walkthrough, onVerify, onClose, onRedeemCode, onFocusPosition }:CodeRedeemProps) => {
    const C = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ show_reward, setShowReward ] = useState(false);
    const [ action_loading, setActionLoading ] = useState(false);
    const [ data, setData ] = useState<{
        code_request?:CodeRequestProps,
        promo?:PromoProps,
        player_referral?:PlayerReferralProps,
        reward_option?:RewardOptionProps,
        referrer?:PublicPlayerProps
    }>({})
    const [ code_error, setCodeError ] = useState('');
    const [ draft_code, setDraftCode ] = useState<string>('') 

    const { code_request, promo, reward_option, referrer } = data;

    useEffect(() => {
        getDataFromServer()
    },[])

    const getDataFromServer = async() => {
        setLoading(true);
        const request_response = await ProfileApi.getMyCodeDetails();
        setData({ ...request_response })
        if(request_response.code_request){ setDraftCode(request_response.code_request.referral_code) }
        setLoading(false);
    }

    const handleTryCodeRequest = async() => {
        if(!draft_code){ setCodeError('Please enter a code') }
        const request_response = await ProfileApi.tryCodeRequest(draft_code);
        if(request_response.result == 'failed'){
            return setCodeError(request_response.error_message ?? 'Unable to use this code')
        }
        setCodeError('');
        return setData({ ...request_response })
    }

    const handleRedeemCode = async() => {
        if(!code_request || action_loading){ return }
        setActionLoading(true)
        const request_response = await ProfileApi.redeemCode(code_request.code_request_id)
        setShowReward(true);
        setTimeout(() => {
            setData({ ...request_response })
            onRedeemCode(request_response)
            setActionLoading(false);
        }, 2000);
    }

    const is_changed = draft_code && draft_code.toLowerCase() != code_request?.referral_code.toLowerCase() ? true : false
    const try_allowed = draft_code ? true : false
    const change_allowed = !loading && ProfileHelpers.isChangeCodeAllowed(code_request) ? true : false


    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>How Did You Find Us?</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>All new players are eligible for a one-time sign up bonus. Below is the code / referrer used during sign up.</Text>
                    </View>
                )
            case 'code_used':
                return (
                    <View style={{ padding:10, marginTop:20 }}>
                        <Text size={20} theme='h1' textAlign='center'>CODE USED</Text>
                        <Text size={14} theme='description' style={{ marginTop:4 }} textAlign='center'>Enter / change the code below</Text>
                        <View type='input' style={{ marginTop:20, flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.USDIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                            
                                value={draft_code}
                                onFocusPosition={onFocusPosition}
                                style={{ flex:1, textAlign: 'center' }}
                                placeholder='Add Code Here'
                                editable={change_allowed}
                                transparent
                                onChangeText={(text) => setDraftCode(text)}
                            />
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                {loading ?
                                <ActivityIndicator size='small' color={C.text.h1}/>
                                :
                                <View transparent>
                                    { !is_changed && draft_code ?
                                    <Icons.CheckIcon size={16} color={C.text.success} />
                                    : code_error ?
                                    <Icons.AlertIcon size={16} color={C.text.warning} />
                                    :
                                    <Icons.CloseIcon size={14} color={C.text.error} />
                                    }
                                </View>
                                }
                            </View>
                        </View>
                        {code_error ?
                        <Text style={{ padding:5 }} theme='error' textAlign='center'>{code_error}</Text>
                        :<></>}
                        {is_changed ?
                            <Button
                                title='Apply Code'
                                style={{ marginTop:20, opacity:try_allowed?1:0.5 }}
                                disabled={!try_allowed}
                                onPress={() => handleTryCodeRequest()}
                                type='success'
                                
                            />
                            :<></>}
                        {false ?
                        <Text theme='warning'>{'ERROR GOES HERE'}</Text>
                        :<></>}
                    </View>
                )
            case 'redeemed':
                return (
                    <View style={{ padding:20 }}>
                        {!reward_option ?
                        <View float style={{ padding:10, marginTop:20 }}>
                            <Text size={20} weight='bold' textAlign='center' theme='h1'>CODE OFFER</Text>
                            {promo ?
                            <View transparent style={{ marginTop:20, borderRadius:8, padding:10 }}>
                                <Text style={{ marginTop:5 }} theme='description' textAlign='center'>{ProfileHelpers.getReferralDescription(promo)}</Text>
                            </View>
                            :<></>}
                        </View>
                        :
                        <View float style={{ padding:10, marginTop:20 }}>
                            <Text size={20} theme='h1' textAlign='center'>CODE REDEEMED</Text>
                            {reward_option ?
                            <View transparent style={{ marginTop:10, padding:10, borderRadius:8 }}>
                                <Text size={14} theme='h2'>AWARDED: {reward_option.reward_name}</Text>
                                {code_request && promo && ['referral_sweepstakes','referral_code'].includes(promo.type) ?
                                <Text style={{ marginTop:10 }} theme='h2'>KICKBACK STATUS: {code_request.status == 'fulfilled' ? 'Referrer will get the same reward amount after you make your first deposit': 'Kickback received'}</Text>
                                :<></>}
                            </View>
                            :<></>}
                        </View>
                        }
                    </View>
                )
            case 'referrer':
                if(!referrer){ return <></> }
                return (
                    <View float style={{ margin:20, padding:5 }}>
                        <View nativeID='referrer_details' style={{ flexDirection:'row', padding:5}}>
                            <Image
                                source={{ uri:referrer.profile_pic }}
                                style={{ height:50, width:50, borderRadius:8 }}
                                resizeMode='cover'
                            />
                            <View style={{ flex:1, marginLeft:20 }}>
                                <Text size={16} theme='h1'>Referred By: {referrer.first_name} {referrer.last_name}</Text>
                                <Text style={{ marginTop:5 }} theme='description'>@{referrer.username}</Text>
                            </View>
                        </View>
                    </View>
                )
            default: return <></>
        }
    }


    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'code_sections'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            <View style={{ flexDirection:'row', padding:10 }}>
                <Button
                    title={walkthrough ? 'SKIP' : 'CLOSE'}
                    style={{ flex:1, marginRight:5 }}
                    type='close'
                    onPress={() =>  onClose()}
                />
                {code_request?.status == 'requested' ?
                    <Button
                        title='VERIFY MY ACCOUNT'
                        style={{ flex:3 }}
                        type='success'
                        onPress={() => onVerify()}
                    />
                    :code_request?.status == 'verified' ?
                    <Button
                        title='REDEEM CODE'
                        style={{ flex:3 }}
                        type='success'
                        onPress={() => handleRedeemCode()}
                    />
                    :
                    <Button
                        title='CODE REDEEMED'
                        style={{ flex:3 }}
                        disabled
                        type='info'
                        onPress={() => console.log('REDEEMED!!!')}
                    />
                    }
                </View>
                {show_reward ?
                <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                    <View float style={{ padding:20, minWidth:250, minHeight:200 }}>
                        {reward_option ?
                        <View transparent>
                            <Text size={14} theme='h1' textAlign='center'>CONGRATULATIONS!</Text>
                            <Text style={{ marginTop:10 }} size={30} theme='success' weight='bold' textAlign='center'>{reward_option.reward_name}</Text>
                            <Text style={{ marginTop:10 }} size={14} theme='description' textAlign='center'>AWARD RECEIVED</Text>
                            <Button
                                title='CLOSE'
                                style={{ marginTop:20 }}
                                type='close'
                                onPress={() => setShowReward(false)}
                            />
                        </View>
                        :
                        <View transparent>
                            <Text size={14} theme='h1' textAlign='center'>GETTING YOUR REWARD...</Text>
                            <ActivityIndicator size='large' color={C.text.h1} style={{alignSelf:'center', marginTop:20 }}/>
                        </View>
                        }
                    </View>
                    :<></>
                </View>
                :<></>}
        </View>
    )
}

export default CodeRedeem